import { Button, IconButton, Popover, CircularProgress, Chip, ChipProps } from '@mui/material'
import { Outlet, useMatch, useNavigate } from 'react-router-dom'
import { TextField } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { ReactComponent as Search } from 'assets/icons/search.svg'
import { useOrders } from 'data/orders'
import ErrorFallback from 'components/ErrorFallback'
import EmptyState from 'components/EmptyState'
import { formatCurrency } from 'utils'
import PageTransition from 'components/PageTransition'
import { DateRangeProps } from 'react-date-range'
import { addDays, format } from 'date-fns'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Filter from 'components/orders/Filter'
import TopNav from 'components/TopNav'
import { Refresh } from 'components/PullToRefresh'

const chipColor = {
  released: 'success',
  completed: 'success',
  cancelled: 'error',
}

export function OrdersList(props: { pharmacyId?: string; isCompleted: boolean }) {
  const { pharmacyId, isCompleted } = props
  const match = useMatch('/easy-med/:status')
  const eazyMedOrders = match?.params.status === 'ongoing' || match?.params.status === 'completed'

  const navigate = useNavigate()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [selectionRange, setSelectionRange] = useState<DateRangeProps['ranges'] | undefined>()
  const sameRangeDate = selectionRange?.[0].startDate === selectionRange?.[0].endDate
  const formattedStartDate = selectionRange
    ? format(selectionRange?.[0].startDate as Date, 'yyyy-MM-dd')
    : undefined
  const formattedEndDate = selectionRange
    ? format(addDays(selectionRange?.[0].endDate as Date, 1), 'yyyy-MM-dd')
    : undefined
  const {
    data: orders,
    isLoading,
    isError,
    refetch,
  } = useOrders(
    isCompleted
      ? ['delivered', 'picked-up', 'completed', 'cancelled', 'failed']
      : [
          'pending',
          'submitted',
          'ready',
          'dispatched',
          'released',
          'shipment-created',
          'shipment-accepted',
          'rider-assigned',
          'rider-arrived-store',
          'rider-in-transit',
          'rider-delivered',
          'rider-arrived',
        ],
    paginationModel.page * paginationModel.pageSize,
    paginationModel.pageSize,
    pharmacyId,
    {
      from_date: sameRangeDate ? undefined : formattedStartDate,
      to_date: sameRangeDate ? undefined : formattedEndDate,
      date: sameRangeDate ? formattedStartDate : undefined,
    }
  )

  const columns: GridColDef[] = [
    {
      field: 'order_id',
      headerName: 'Order ID',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    {
      field: 'item_count',
      headerName: 'Item Count',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    {
      field: 'subtotal',
      headerName: 'Order Total',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    {
      field: 'dispensed_by',
      headerName: 'Dispensed By',
      flex: 1,
      sortable: false,
      headerClassName: 'pl-2',
    },
    ...(isCompleted
      ? [
          {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            sortable: false,
            renderCell: ({ row }: { row: { status: string } }) => (
              <Chip
                label={row.status}
                color={chipColor[row?.status as keyof typeof chipColor] as ChipProps['color']}
              />
            ),
          },
        ]
      : []),
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
        return (
          <>
            <IconButton
              aria-label={`View actions for order ${row.order_id}`}
              onClick={(event) => {
                setAnchorEl(event.currentTarget as HTMLElement)
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{
                paper: {
                  style: {
                    backgroundColor: 'transparent',
                    border: 0,
                  },
                },
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <div className="bg-brand-900 h-fit rounded flex flex-col p-5 space-y-3">
                <button
                  className="text-white text-sm"
                  onClick={() => {
                    navigate(`${row.order_id}`)
                  }}
                >
                  View Details
                </button>
              </div>
              <div className="relative  mt-[-15px]">
                <ArrowDropDownIcon className="text-4xl text-brand-900" />
              </div>
            </Popover>
          </>
        )
      },
    },
  ]

  const rows =
    orders?.items?.map((item, index) => {
      return {
        id: index,
        order_id: item?.id,
        item_count: item.item_count,
        subtotal: formatCurrency(Number(item.sum_total)),
        dispensed_by: `${item.pharmacy_name}`,
        status: item.order_status,
      }
    }) ?? []

  return (
    <>
      <PageTransition show={eazyMedOrders}>
        <TopNav title="Order History" />
        <Refresh refetch={refetch}>
          <div className="lg:hidden">
            <div className="mb-4">
              <Filter
                filterByDate={(data) => setSelectionRange(data as DateRangeProps['ranges'])}
                dateRange={selectionRange}
                key={JSON.stringify(selectionRange)}
              />
            </div>
            {isLoading ? (
              <div className="grid place-items-center mt-40">
                <CircularProgress />
              </div>
            ) : isError ? (
              <ErrorFallback
                message="Something went wrong"
                description="We could not fetch Orders"
                reset={refetch}
                buttonText="Try again"
              />
            ) : orders?.item_count === 0 ? (
              <EmptyState text="There are no Orders yet" />
            ) : (
              <div className="lg:hidden">
                {orders?.items?.map((item) => {
                  return (
                    <div
                      className="rounded-xl p-5 border border-default font-medium space-y-4  mb-5"
                      key={item.id}
                    >
                      <p>Order Code - {item.order_code}</p>
                      <p>Item Count - {item.item_count}</p>
                      {!location.pathname.includes('provider') && (
                        <p>Processed By - {item.pharmacy_name}</p>
                      )}
                      <p>Order Total -{formatCurrency(Number(item.sum_total))}</p>
                      {isCompleted && (
                        <Chip
                          label={item.order_status}
                          color={
                            chipColor[
                              item?.order_status as keyof typeof chipColor
                            ] as ChipProps['color']
                          }
                        />
                      )}{' '}
                      <div className="flex justify-end">
                        <Button
                          variant="text"
                          className="px-3 text-brand-700"
                          onClick={() => navigate(`${item.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  )
                })}
                {orders && (
                  <div className="justify-between text-neutral-500 mt-20 flex">
                    <Button
                      startIcon={<ArrowBackIcon />}
                      variant="outlined"
                      onClick={() => {
                        setPaginationModel({
                          ...paginationModel,
                          page: paginationModel.page - 1,
                        })
                      }}
                      disabled={paginationModel.page + 1 === 1}
                      className="px-3 border border-default text-neutral-500"
                    >
                      Previous
                    </Button>
                    <div className="page-number">
                      Page {paginationModel.page + 1} of{' '}
                      {Math.ceil(orders?.item_count / paginationModel.pageSize)}
                    </div>
                    <Button
                      endIcon={<ArrowForwardIcon className="text-error-900" />}
                      variant="outlined"
                      onClick={() => {
                        setPaginationModel({
                          ...paginationModel,
                          page: paginationModel.page + 1,
                        })
                      }}
                      disabled={
                        paginationModel.page + 1 ===
                        Math.ceil(orders?.item_count / paginationModel.pageSize)
                      }
                      className="px-3 py-1 border border-error-600 text-error-900"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Refresh>
        <div className="lg:block hidden" draggable="false">
          <div className="rounded z-50" draggable="false">
            {isLoading ? (
              <div className="grid place-items-center mt-12 mb-10">
                <CircularProgress />
              </div>
            ) : isError ? (
              <ErrorFallback
                message="Something went wrong"
                description="We could not fetch orders"
                reset={refetch}
                buttonText="Try again"
              />
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      dispensed_by: location.pathname.includes('provider') ? false : true,
                    },
                  },
                }}
                rowCount={orders?.item_count || 0}
                loading={isLoading}
                paginationModel={paginationModel}
                paginationMode="server"
                onPaginationModelChange={setPaginationModel}
                sx={{ '& .MuiDataGrid-columnSeparator': { display: 'none' } }}
                slots={{
                  toolbar: () => (
                    <div className="flex justify-between py-8 items-center px-4">
                      <TextField
                        id="name"
                        type="text"
                        placeholder="Search"
                        className="w-1/2"
                        InputProps={{
                          sx: {
                            '& input': {
                              '&:focus': {
                                backgroundColor: 'white',
                              },
                            },
                          },
                          className: 'p-0 overflow-hidden',
                          startAdornment: (
                            <Button className={`bg-white`}>
                              <Search className={`text-neutral-500 w-6 h-6`} />
                            </Button>
                          ),
                        }}
                      />
                      <Filter
                        filterByDate={(data) => setSelectionRange(data as DateRangeProps['ranges'])}
                        dateRange={selectionRange}
                      />
                    </div>
                  ),
                }}
                onRowClick={({ row }, event) => {
                  if (!(event.target instanceof SVGElement)) {
                    navigate(`${row.order_id}`)
                  }
                }}
                pageSizeOptions={[5, 10, 15, 20, 25]}
                disableColumnMenu
              />
            )}
          </div>
        </div>
      </PageTransition>
      <PageTransition show={!eazyMedOrders}>
        <Outlet />
      </PageTransition>
    </>
  )
}
